import { createPrng } from '@play-co/replicant';
import { State } from 'src/replicant/State';
import {
  CardId,
  CardTier,
  CardsLevelWinDropSource,
  CardsInventory,
  CardsRewardId,
  CardsRewardStatus,
  cardsCatalog,
  cardsCollectionIds,
  cardsUnlockLevel,
  cardTradeMaxOpenTrades,
  CollectionId,
  tradeLimits,
  cardCollections,
  cardTiers,
  cardsInviteMintableTiers,
  cardsLevelWinDropConfig,
  cardTradeExpirationMs,
} from './cards.ruleset';
import type { CardPendingTrade } from './cards.schema';
import type {
  CardsDonateShareArgs,
  CardsDonateShareValidationResult,
  CardsInviteRequestArgs,
  CardsInviteRequestValidationResult,
  CardsTradeArgs,
  CardsTradeValidationResult,
} from './cards.types';
import { getJSTDateTime } from '../utils/time';
import type { CardReward } from '../match2/rewards/match2-rewards.ruleset';
import { weightedRandom } from '../utils/random';
import features from '../ruleset/features';
import {
  canAddFriends,
  isBuruFriend,
  wasUnfriended,
} from '../friends/friends.getters';

/**
 * Build a share-trade ID from the sender's current Cards state and timestamp.
 *
 * @param state Sender state that may already contain a pending trade.
 * @param now Current client or server time.
 * @returns A trade identifier that does not collide with a pending trade.
 */
export function cardsCreateShareTradeId(state: State, now: number): string {
  const pendingTrades = state.cards?.cardTrades?.pendingTrades ?? {};
  const baseTradeId = `ctr_share_${now}_${state.id}`;
  let tradeId = baseTradeId;
  let suffix = 1;
  while (pendingTrades[tradeId]) {
    tradeId = `${baseTradeId}_${suffix}`;
    suffix += 1;
  }
  return tradeId;
}

/**
 * Return whether the cards collection feature is enabled for this user.
 *
 * @param state User state used to evaluate cards feature flags.
 * @returns True when cards collection V2 is enabled.
 */
export function cardsIsEnabled(state: State): boolean {
  return features(state).cardsCollectionV2 === true;
}

/**
 * Return whether the cards collection feature is unlocked and usable.
 * The feature must be enabled and the user must have reached the unlock level.
 *
 * @param state User state containing feature flags and match2 progress.
 * @param match2Level Match2 progression level to evaluate. Defaults to state.
 * @returns True when the feature is enabled and the user reached the unlock level.
 */
export function cardsIsAvailable(state: State, match2Level?: number): boolean {
  // Use the user's current progression when no explicit level is provided.
  const evaluatedMatch2Level = match2Level ?? state.match2.level;

  // The feature flag must be enabled before Cards can become available.
  if (!cardsIsEnabled(state)) {
    return false;
  }

  // Cards become available when progression reaches the configured unlock level.
  return evaluatedMatch2Level >= cardsUnlockLevel;
}

/**
 * Return whether the user owns at least one card in their inventory.
 *
 * @param state User state containing the cards inventory.
 * @returns True when any owned-card count is greater than zero.
 */
export function cardsHasOwnedCards(state: State): boolean {
  const cardsOwned = state.cards?.cardsOwned;
  if (!cardsOwned) return false;

  return Object.values(cardsOwned).some((count) => count > 0);
}

/**
 * Return whether the cards HUD entry point should be visible.
 * Enabled users can see it after unlock, or before unlock when they have
 * card-related state that needs an entry point.
 *
 * @param state User state containing feature flags, progress, and inventory.
 * @param now Current timestamp used to filter pending trades.
 * @returns True when the cards HUD button should be shown.
 */
export function cardsShouldShowHudButton(state: State, now: number): boolean {
  if (!cardsIsEnabled(state)) return false;

  // Keep the Cards HUD hidden during levels 1 and 2 because they are FTUE tutorial levels.
  if (state.match2.level <= 2) return false;
  if (cardsIsAvailable(state)) return true;

  if (cardsHasOwnedCards(state)) return true;
  if (
    Object.values(cardsGetReceivedCards(state, now)).some((cards) =>
      Object.values(cards).some((count) => count > 0),
    )
  ) {
    return true;
  }

  return cardsHasAnyPendingTrades(state, now);
}

/**
 * Return whether the visible cards HUD entry point should show a locked state.
 *
 * @param state User state containing feature flags, progress, and inventory.
 * @param now Current timestamp used to filter pending trades.
 * @returns True when the HUD button is visible but cards are not available yet.
 */
export function cardsIsLocked(state: State, now: number): boolean {
  return cardsShouldShowHudButton(state, now) && !cardsIsAvailable(state);
}

/**
 * Return whether the cards feature should auto-open for first-time discovery.
 *
 * @param state User state containing cards availability and seen state.
 * @returns True when Cards is unlocked and the user has never opened it.
 */
export function cardsShouldAutoOpenFirstTime(state: State): boolean {
  return cardsIsAvailable(state) && !state.cards?.seenAt;
}

/**
 * Check whether an arbitrary string is a valid card catalog id.
 *
 * @param cardId Candidate card catalog id.
 * @returns True when the id exists in the cards catalog.
 */
export function cardsIsCardValid(cardId: string): cardId is CardId {
  return Object.prototype.hasOwnProperty.call(cardsCatalog, cardId);
}

/**
 * Build the card rewards for a level win.
 * A player without owned cards receives a guaranteed legendary card. Later
 * drops require an eligible source and successful configured drop roll.
 *
 * @param state User state containing cards availability and match2 streak state.
 * @param args Level-win context used for deterministic drop selection.
 * @param args.now Current timestamp used as part of the deterministic seed.
 * @param args.winStreak User win streak for tier weighting.
 * @param args.hasContinued Whether the client knows the level used a continue.
 * @returns A card reward, or an empty array when no card should be granted.
 */
export function cardsGetLevelWinRewards(
  state: State,
  args: { now: number; winStreak: number; hasContinued?: boolean },
): CardReward[] {
  // Evaluate availability against the progression reached by the current win.
  const levelAfterWin = state.match2.level + 1;
  if (!cardsIsAvailable(state, levelAfterWin)) return [];

  // Classify the win as a guaranteed first drop or an ordinary eligible source.
  const source = cardsGetLevelWinDropSource(state, {
    hasContinued: args.hasContinued,
  });
  if (!source) return [];

  // Every eligible source must pass its configured drop probability.
  if (!cardsShouldDropLevelWinCard(state, { ...args, source })) return [];

  // Guarantee a legendary first card; otherwise use streak-weighted tiers.
  const cardId = cardsPickLevelWinCard(state, {
    now: args.now,
    winStreak: args.winStreak,
    dropIndex: 0,
    forcedTier: source === 'firstDrop' ? 'legendary' : undefined,
  });

  // No reward can be produced when the selected tier has no configured cards.
  if (!cardId) return [];

  // Return the selected card through the existing Match2 reward pipeline.
  return [{ type: 'card', cardId }];
}

/**
 * Classify why a level win is eligible for a card drop.
 * Cardless players receive a guaranteed first drop. Later drops prioritize
 * Replicant-tracked gem continues, then first-attempt wins.
 *
 * @param state User state containing match2 continue and losing-streak state.
 * @param args Optional client-side continue context.
 * @returns The eligible drop source, or undefined when the win is not eligible.
 */
export function cardsGetLevelWinDropSource(
  state: State,
  args?: { hasContinued?: boolean },
): CardsLevelWinDropSource | undefined {
  // Cardless players should receive the guaranteed first-drop reward.
  if (!cardsHasOwnedCards(state)) {
    return 'firstDrop';
  }

  // Replicant-tracked gem continues use their configured drop probability.
  if (state.match2.continues > 0) {
    return 'gemContinue';
  }

  // First-attempt wins use their configured drop probability.
  if (!args?.hasContinued && state.match2.losingStreak === 0) {
    return 'firstAttempt';
  }

  // Other retries and continues are not eligible for an ordinary card drop.
  return undefined;
}

/**
 * Roll the deterministic per-source drop chance for an eligible level win.
 *
 * @param state User state whose id contributes to the deterministic seed.
 * @param args Drop-roll context.
 * @param args.now Current timestamp used as part of the deterministic seed.
 * @param args.source Drop source used to select the configured drop chance.
 * @param args.winStreak Win streak carried with the level-win context, but not
 * used by this roll.
 * @returns True when the seeded roll is below the configured drop chance.
 */
function cardsShouldDropLevelWinCard(
  state: State,
  args: { now: number; winStreak: number; source: CardsLevelWinDropSource },
): boolean {
  const dropChancePercent =
    cardsLevelWinDropConfig.dropChancePercentBySource[args.source];
  const random = createPrng(`${state.id}-cards-level-win-drop-${args.now}`);

  return random() * 100 < dropChancePercent;
}

/**
 * Pick a deterministic card for a level-win drop.
 * The user's win streak selects tier weights unless a required tier is supplied,
 * and the card is drawn from all configured cards in the selected tier.
 *
 * @param state User state whose id contributes to the deterministic seed.
 * @param args Level-win context used for tier weighting and deterministic choice.
 * @param args.now Current timestamp used as part of the deterministic seed.
 * @param args.winStreak User win streak for tier weighting.
 * @param args.dropIndex Index for disambiguating multiple drops in one moment.
 * @param args.forcedTier Required tier for guaranteed rewards, when applicable.
 * @returns The selected card id, or undefined if no configured card is available.
 */
export function cardsPickLevelWinCard(
  state: State,
  args: {
    now: number;
    winStreak: number;
    dropIndex: number;
    forcedTier?: CardTier;
  },
): CardId | undefined {
  // Clamp the streak before using it in weights or deterministic seed data.
  const winStreak = Math.max(args.winStreak, 1);

  // Resolve the configured tier weights for ordinary level-win drops.
  const tierWeights = cardsGetLevelWinTierWeights(winStreak);

  // Seed card selection consistently for this player and level-win moment.
  const random = createPrng(
    `${state.id}-cards-level-win-${args.now}-${winStreak}-${args.dropIndex}`,
  );

  // Use the required tier for guarantees or roll the ordinary streak weights.
  const tier =
    args.forcedTier ??
    cardTiers[
      weightedRandom(
        cardTiers.map((cardTier) => tierWeights[cardTier]),
        random,
      )
    ];

  // Build the pool from every active collection in the selected tier.
  const availableCards = cardsCollectionIds.flatMap((collectionId) =>
    cardCollections[collectionId].cards.filter(
      (cardId) => cardsCatalog[cardId].tier === tier,
    ),
  );

  // No reward can be produced when the selected tier has no configured cards.
  if (!availableCards.length) return undefined;

  // Return one deterministic random card from the selected tier.
  return availableCards[(random() * availableCards.length) | 0];
}

/**
 * Pick the card awarded to a new invitee through a card request link.
 *
 * Requests for mintable tiers grant the requested card. Other valid requests
 * grant a deterministic mintable fallback so preview and redeem agree.
 *
 * @param state Invitee state whose id contributes to the deterministic seed.
 * @param args Request details from the card invite link.
 * @param args.inviterId Player id for the inviter who shared the request link.
 * @param args.cardId Requested card id from the invite link.
 * @returns The granted card id, or undefined when no mintable card is available.
 */
export function cardsPickInviteGrantCard(
  state: State,
  args: { inviterId: string; cardId: CardId },
): CardId | undefined {
  if (cardsInviteMintableTiers.includes(cardsCatalog[args.cardId].tier)) {
    return args.cardId;
  }

  const availableCards = cardsCollectionIds.flatMap((collectionId) =>
    cardCollections[collectionId].cards.filter((cardId) =>
      cardsInviteMintableTiers.includes(cardsCatalog[cardId].tier),
    ),
  );

  if (!availableCards.length) return undefined;

  const random = createPrng(
    `${state.id}-cards-invite-${args.inviterId}-${args.cardId}`,
  );
  return availableCards[(random() * availableCards.length) | 0];
}

/**
 * Validate card-request link integrity before checking player state.
 *
 * @param state Invitee opening the request link.
 * @param args Request link arguments.
 * @returns Whether the link data is valid.
 */
export function cardsValidateInviteRequestLink(
  state: State,
  args: CardsInviteRequestArgs,
): CardsInviteRequestValidationResult {
  if (!args.inviterId || args.inviterId === state.id) {
    return { valid: false, reason: 'invalid_link' };
  }

  if (!cardsIsCardValid(args.cardId)) {
    return { valid: false, reason: 'invalid_link' };
  }

  return { valid: true };
}

/**
 * Validate whether an invitee can redeem a card request before fetching the
 * inviter state.
 *
 * @param state Invitee opening the request link.
 * @param args Request link arguments.
 * @param now Current timestamp used for send-limit checks.
 * @returns Whether the invitee can use the mint-and-send redemption route.
 */
export function cardsValidateInviteRequestRedemption(
  state: State,
  args: CardsInviteRequestArgs,
  now: number,
): CardsInviteRequestValidationResult {
  // Redemption is reserved for invitees who do not already own cards.
  if (cardsHasOwnedCards(state)) {
    return { valid: false, reason: 'request_unavailable' };
  }

  // Redemption still counts as a card send and must respect its limit.
  if (!cardsCanSendCard(state, now)) {
    return { valid: false, reason: 'send_limit_reached' };
  }

  // Previously removed friends cannot reconnect through a request link.
  const isAlreadyFriend = isBuruFriend(state, args.inviterId);
  if (wasUnfriended(state, args.inviterId)) {
    return { valid: false, reason: 'friend_unavailable' };
  }

  // Redemption must leave room to connect both players when needed.
  if (!isAlreadyFriend && !canAddFriends(state)) {
    return { valid: false, reason: 'friend_unavailable' };
  }

  // All invitee-side redemption checks passed.
  return { valid: true };
}

/**
 * Validate the inviter side of a card request after fetching inviter state.
 *
 * @param state Invitee opening the request link.
 * @param inviterState Fetched inviter state.
 * @param args Request link arguments.
 * @returns Whether the inviter can receive the request card and friendship.
 */
export function cardsValidateInviteRequestOther(
  state: State,
  inviterState: State,
  args: CardsInviteRequestArgs,
): CardsInviteRequestValidationResult {
  if (inviterState.id !== args.inviterId) {
    return { valid: false, reason: 'requester_unavailable' };
  }

  const inviterAlreadyFriend = isBuruFriend(inviterState, state.id);
  if (wasUnfriended(inviterState, state.id)) {
    return { valid: false, reason: 'requester_unavailable' };
  }

  if (!inviterAlreadyFriend && !canAddFriends(inviterState)) {
    return { valid: false, reason: 'requester_unavailable' };
  }

  return { valid: true };
}

/**
 * Validate donated-card link integrity before checking player state.
 *
 * @param state Receiver opening the donation link.
 * @param args Donation link arguments.
 * @returns Whether the link data is valid.
 */
export function cardsValidateDonateShareLink(
  state: State,
  args: CardsDonateShareArgs,
): CardsDonateShareValidationResult {
  if (!args.senderId || args.senderId === state.id) {
    return { valid: false, reason: 'invalid_link' };
  }

  if (!cardsIsCardValid(args.cardId)) {
    return { valid: false, reason: 'invalid_link' };
  }

  return { valid: true };
}

/**
 * Validate the receiver side of a donation before fetching sender state.
 *
 * @param state Receiver claiming the donated card.
 * @param args Donation link arguments.
 * @param now Current timestamp used for ownership and receive-limit checks.
 * @returns Whether the receiver can claim the donated card.
 */
export function cardsValidateDonateShareSelf(
  state: State,
  args: CardsDonateShareArgs,
  now: number,
): CardsDonateShareValidationResult {
  if (cardsHasCardOrPendingReceived(state, args.cardId, now)) {
    return { valid: false, reason: 'already_have_card' };
  }

  if (!cardsCanReceiveCard(state, now)) {
    return { valid: false, reason: 'receiver_receive_limit_reached' };
  }

  return { valid: true };
}

/**
 * Validate the sender side of a donation after fetching sender state.
 *
 * @param senderState Fetched sender state.
 * @param args Donation link arguments.
 * @param now Current timestamp used for ownership and send-limit checks.
 * @returns Whether the sender can donate the requested card.
 */
export function cardsValidateDonateShareOther(
  senderState: State,
  args: CardsDonateShareArgs,
  now: number,
): CardsDonateShareValidationResult {
  if (senderState.id !== args.senderId) {
    return { valid: false, reason: 'sender_unavailable' };
  }

  if (!cardsCanSendCard(senderState, now)) {
    return { valid: false, reason: 'sender_send_limit_reached' };
  }

  if (cardsGetOwnedCardCount(senderState, args.cardId, now) < 2) {
    return { valid: false, reason: 'sender_unavailable' };
  }

  return { valid: true };
}

/**
 * Validate card-trade link integrity before checking player state.
 *
 * @param state Opener using the trade link.
 * @param args Trade link arguments.
 * @returns Whether the link data is valid.
 */
export function cardsValidateTradeLink(
  state: State,
  args: CardsTradeArgs,
): CardsTradeValidationResult {
  // New trade links must carry the client-generated share-trade ID format.
  const hasInvalidTradeId =
    args.tradeId !== undefined &&
    !/^ctr_share_\d+_[A-Za-z0-9_-]+$/.test(args.tradeId);
  if (hasInvalidTradeId) {
    return { valid: false, reason: 'invalid_link' };
  }

  // A link cannot be opened by its own sender.
  if (!args.senderId || args.senderId === state.id) {
    return { valid: false, reason: 'invalid_link' };
  }

  if (!cardsIsCardValid(args.offeredCardId)) {
    return { valid: false, reason: 'invalid_link' };
  }

  if (args.counterCardId && !cardsIsCardValid(args.counterCardId)) {
    return { valid: false, reason: 'invalid_link' };
  }

  return { valid: true };
}

/**
 * Validate the opener side of a trade before fetching sender state.
 *
 * @param openerState Opener using the trade link.
 * @param args Trade link arguments.
 * @param now Current timestamp used for trade and inventory checks.
 * @returns Whether the opener can create the trade.
 */
export function cardsValidateTradeSelf(
  openerState: State,
  args: CardsTradeArgs,
  now: number,
): CardsTradeValidationResult {
  if (
    cardsGetPendingTrades(openerState, now).length >= cardTradeMaxOpenTrades
  ) {
    return { valid: false, reason: 'opener_trade_limit_reached' };
  }

  if (
    args.counterCardId &&
    cardsGetOwnedCardCount(openerState, args.counterCardId, now) < 2
  ) {
    return { valid: false, reason: 'counter_card_unavailable' };
  }

  return { valid: true };
}

/**
 * Validate the sender side of a trade after fetching sender state.
 *
 * @param senderState Fetched sender state.
 * @param args Trade link arguments.
 * @param now Current timestamp used for trade, send, and receive limits.
 * @returns Whether the sender can trade the offered card.
 */
export function cardsValidateTradeOther(
  senderState: State,
  args: CardsTradeArgs,
  now: number,
): CardsTradeValidationResult {
  if (senderState.id !== args.senderId) {
    return { valid: false, reason: 'sender_unavailable' };
  }

  if (cardsGetOwnedCardCount(senderState, args.offeredCardId, now) < 2) {
    return { valid: false, reason: 'sender_unavailable' };
  }

  const numPendingTrades = cardsGetPendingTrades(senderState, now).length;
  if (numPendingTrades >= cardTradeMaxOpenTrades) {
    return { valid: false, reason: 'sender_trade_limit_reached' };
  }

  if (!cardsCanStartTrade(senderState, now)) {
    return { valid: false, reason: 'sender_trade_limit_reached' };
  }

  if (!cardsCanSendCard(senderState, now)) {
    return { valid: false, reason: 'sender_send_limit_reached' };
  }

  if (!cardsCanReceiveCard(senderState, now)) {
    return { valid: false, reason: 'sender_receive_limit_reached' };
  }

  return { valid: true };
}

/**
 * Return the tier weights for the highest streak bracket reached.
 *
 * @param winStreak User win streak after the level win.
 * @returns Tier weights for the matching streak bracket.
 */
function cardsGetLevelWinTierWeights(
  winStreak: number,
): Record<CardTier, number> {
  let weights = cardsLevelWinDropConfig.streakTierWeights[0].weights;

  for (const entry of cardsLevelWinDropConfig.streakTierWeights) {
    if (winStreak >= entry.minWinStreak) {
      weights = entry.weights;
    }
  }

  return weights;
}

/**
 * Return the user's owned-card inventory.
 *
 * @param state User state containing the cards inventory.
 * @returns Owned-card counts keyed by card id.
 */
export function cardsGetOwnedCards(state: State): CardsInventory {
  return state.cards?.cardsOwned ?? {};
}

/**
 * Return sorted ids for every catalog card the user does not own.
 *
 * @param state User state containing the cards inventory.
 * @param _now Current timestamp, accepted for getter API parity.
 * @returns Sorted catalog card ids with no owned copy.
 */
export function cardsGetMissingCardIds(state: State, _now: number): CardId[] {
  const cardsOwned = cardsGetOwnedCards(state);
  return (Object.keys(cardsCatalog) as CardId[])
    .filter((cardId) => (cardsOwned[cardId] ?? 0) <= 0)
    .sort();
}

/**
 * Return how many copies of a specific card the user owns.
 *
 * @param state User state containing the cards inventory.
 * @param cardId Card id to count.
 * @param _now Current timestamp, accepted for getter API parity.
 * @returns Number of owned copies for the card.
 */
export function cardsGetOwnedCardCount(
  state: State,
  cardId: CardId,
  _now: number,
): number {
  return cardsGetOwnedCards(state)[cardId] || 0;
}

/**
 * Return whether the user owns a card or has it waiting in the received-card
 * ledger.
 *
 * @param state User state containing owned and received cards.
 * @param cardId Card id to check.
 * @param now Current timestamp forwarded to card inventory getters.
 * @returns True when the card is owned or waiting to be collected.
 */
export function cardsHasCardOrPendingReceived(
  state: State,
  cardId: CardId,
  now: number,
): boolean {
  if (cardsGetOwnedCardCount(state, cardId, now) > 0) return true;

  return Object.values(cardsGetReceivedCards(state, now)).some(
    (cards) => (cards[cardId] ?? 0) > 0,
  );
}

/**
 * Get the timestamp when a trade record expires after its latest update.
 *
 * A proposal starts with matching created and updated timestamps. Countering
 * or completing the trade updates `updatedAt`, refreshing its response or
 * verification window.
 *
 * @param trade Pending or completed trade record to inspect.
 * @returns Expiration timestamp in milliseconds.
 */
export function cardsGetTradeExpiresAt(trade: CardPendingTrade): number {
  return trade.updatedAt + cardTradeExpirationMs;
}

/**
 * List currently active pending trades for the user.
 * Completed recipient markers stay in state briefly for proposer verification,
 * but they are hidden from UI callers by `proposerConfirmedAt`.
 *
 * @param state User state containing pending card trades.
 * @param now Current timestamp used to filter expired trades.
 * @returns Active pending trades sorted oldest first.
 */
export function cardsGetPendingTrades(
  state: State,
  now: number,
): CardPendingTrade[] {
  const pendingTrades = state.cards?.cardTrades?.pendingTrades ?? {};
  const trades = Object.values(pendingTrades) as unknown as CardPendingTrade[];
  return trades
    .filter(
      (trade) =>
        cardsGetTradeExpiresAt(trade) > now && !trade.proposerConfirmedAt,
    )
    .sort((a, b) => a.createdAt - b.createdAt);
}

function cardsCanActOnPendingTrade(
  state: State,
  trade: CardPendingTrade,
): boolean {
  if (trade.recipientId === state.id) {
    return trade.status === 'proposed';
  }

  if (trade.proposerId === state.id) {
    return trade.status === 'countered' && !!trade.counterCardIds?.length;
  }

  return false;
}

/**
 * List pending card trades where the user can take an action now.
 *
 * @param state User state containing pending card trades.
 * @param now Current timestamp used to filter expired trades.
 * @returns Actionable pending trades sorted oldest first.
 */
export function cardsGetActionablePendingTrades(
  state: State,
  now: number,
): CardPendingTrade[] {
  return cardsGetPendingTrades(state, now).filter((trade) =>
    cardsCanActOnPendingTrade(state, trade),
  );
}

/**
 * Get a single active pending trade, excluding expired trades and
 * recipient-side completion markers.
 *
 * @param state User state containing pending card trades.
 * @param tradeId Trade id to retrieve.
 * @param now Current timestamp used to filter expired trades.
 * @returns The active trade, or undefined when it is missing or inactive.
 */
export function cardsGetPendingTrade(
  state: State,
  tradeId: string,
  now: number,
): CardPendingTrade | undefined {
  const trade = state.cards?.cardTrades?.pendingTrades?.[tradeId];
  if (
    !trade ||
    trade.proposerConfirmedAt ||
    cardsGetTradeExpiresAt(trade as CardPendingTrade) <= now
  ) {
    return undefined;
  }
  return trade as unknown as CardPendingTrade;
}

/**
 * Compare two card id lists for the exact same valid cards in the same order.
 *
 * @param left First card id list.
 * @param right Second card id list.
 * @returns True when both lists contain the same valid card ids in order.
 */
function cardsHaveSameValidCardIds(
  left: readonly string[] | undefined,
  right: readonly string[] | undefined,
): boolean {
  if (!left?.length || !right?.length) return false;
  if (left.length !== right.length) return false;

  return left.every((leftCardId, index) => {
    const rightCardId = right[index];
    return (
      rightCardId !== undefined &&
      leftCardId === rightCardId &&
      cardsIsCardValid(leftCardId) &&
      cardsIsCardValid(rightCardId)
    );
  });
}

/**
 * Check that proposer and recipient still have the same countered trade before
 * the proposer asks the recipient to complete it.
 *
 * @param proposerState State for the user who proposed the trade.
 * @param recipientState State for the user who received the trade.
 * @param tradeId Trade id to validate in both user states.
 * @param now Current timestamp used to reject expired trades.
 * @returns True when both users still have the same countered pending trade.
 */
export function cardsIsValidPendingTrade(
  proposerState: State,
  recipientState: State,
  tradeId: string,
  now: number,
): boolean {
  const proposerTrade = cardsGetPendingTrade(proposerState, tradeId, now);
  const recipientTrade = cardsGetPendingTrade(recipientState, tradeId, now);

  if (!proposerTrade || !recipientTrade) return false;
  if (proposerTrade.status !== 'countered') return false;
  if (recipientTrade.status !== 'countered') return false;

  if (proposerTrade.proposerId !== proposerState.id) return false;
  if (recipientTrade.recipientId !== recipientState.id) return false;
  if (proposerTrade.proposerId !== recipientTrade.proposerId) return false;
  if (proposerTrade.recipientId !== recipientTrade.recipientId) return false;

  if (
    !cardsHaveSameValidCardIds(
      proposerTrade.offeredCardIds,
      recipientTrade.offeredCardIds,
    )
  ) {
    return false;
  }

  if (
    !cardsHaveSameValidCardIds(
      proposerTrade.counterCardIds,
      recipientTrade.counterCardIds,
    )
  ) {
    return false;
  }

  return true;
}

/**
 * Verify that the recipient already completed their side of a trade.
 * This handles the proposer retry path after the completion message was flushed
 * but before the proposer finished local completion.
 *
 * @param recipientState State for the trade recipient.
 * @param trade Proposer-side trade being checked.
 * @returns True when the recipient has a matching completed trade marker.
 */
export function cardsHasRecipientCompletedTrade(
  recipientState: State,
  trade: CardPendingTrade,
): boolean {
  const recipientTrade =
    recipientState.cards?.cardTrades.pendingTrades?.[trade.tradeId];
  if (!recipientTrade?.proposerConfirmedAt) return false;
  if (recipientTrade.proposerId !== trade.proposerId) return false;
  if (recipientTrade.recipientId !== trade.recipientId) return false;
  if (recipientTrade.recipientId !== recipientState.id) return false;
  if (recipientTrade.createdAt !== trade.createdAt) return false;
  if (recipientTrade.status !== 'countered') return false;

  if (
    !cardsHaveSameValidCardIds(
      recipientTrade.counterCardIds,
      trade.counterCardIds,
    )
  ) {
    return false;
  }

  if (
    !cardsHaveSameValidCardIds(
      recipientTrade.offeredCardIds,
      trade.offeredCardIds,
    )
  ) {
    return false;
  }

  return true;
}

/**
 * Check whether the user has any actionable trade to show in the cards flow.
 *
 * @param state User state containing pending card trades.
 * @param now Current timestamp used to filter expired trades.
 * @returns True when there is at least one actionable pending trade.
 */
export function cardsHasPendingTrades(state: State, now: number): boolean {
  return cardsGetActionablePendingTrades(state, now).length > 0;
}

/**
 * Check whether the user has any active trade, including waiting states.
 *
 * @param state User state containing pending card trades.
 * @param now Current timestamp used to filter expired trades.
 * @returns True when at least one active pending trade exists.
 */
export function cardsHasAnyPendingTrades(state: State, now: number): boolean {
  return cardsGetPendingTrades(state, now).length > 0;
}

/**
 * Return the user's received-card ledger grouped by sender.
 * `now` is accepted for getter API parity but is not used.
 *
 * @param state User state containing received-card records.
 * @param _now Current timestamp, accepted for getter API parity.
 * @returns Received-card counts keyed first by sender id, then by card id.
 */
export function cardsGetReceivedCards(
  state: State,
  _now: number,
): Record<string, CardsInventory> {
  return state.cards?.cardsReceived ?? {};
}

/**
 * Return cards the user can offer in a trade.
 * A card is tradeable only when the user owns at least two copies.
 *
 * @param state User state containing the cards inventory.
 * @param now Current timestamp forwarded to card inventory getters.
 * @returns Owned-card counts for cards that have at least two copies.
 */
export function cardsGetTradeableCards(
  state: State,
  now: number,
): CardsInventory {
  const map: CardsInventory = {};
  const ownedCards = cardsGetOwnedCards(state);
  const ownedCardsIds = Object.keys(ownedCards) as CardId[];
  for (const cardId of ownedCardsIds) {
    const count = cardsGetOwnedCardCount(state, cardId, now);
    if (count >= 2) {
      map[cardId] = count;
    }
  }
  return map;
}

/**
 * Return the claim status for a collection reward or the full album reward.
 * Invalid reward ids and incomplete sets are treated as unavailable.
 *
 * @param state User state containing owned cards and claimed rewards.
 * @param rewardId Collection id or `album` reward id to inspect.
 * @returns Current reward claim status.
 */
export function cardsGetRewardsStatus(
  state: State,
  rewardId: CardsRewardId,
): CardsRewardStatus {
  const cardsState = state.cards;
  if (!cardsState) return 'unavailable';

  if (rewardId !== 'album' && !cardsCollectionIds.includes(rewardId)) {
    return 'unavailable';
  }

  if (cardsState.rewardsClaimed?.[rewardId]) {
    return 'claimed';
  }

  const cardsOwned = cardsState.cardsOwned ?? {};

  if (rewardId === 'album') {
    const allCards = Object.keys(cardsCatalog) as CardId[];
    return allCards.every((cardId) => cardsOwned[cardId])
      ? 'available'
      : 'unavailable';
  }

  return cardCollections[rewardId].cards.every((cardId) => cardsOwned[cardId])
    ? 'available'
    : 'unavailable';
}

/**
 * Get all card collections.
 *
 * @returns Configured card collection ids.
 */
export function cardsGetCollections(): CollectionId[] {
  return cardsCollectionIds;
}

/**
 * Return sent-card trade records that still count against the send limit.
 * Limit windows are measured by JST calendar days.
 *
 * @param state User state containing card send history.
 * @param now Current timestamp used as the limit-window reference.
 * @returns Sent-card trade records still inside the send limit window.
 */
export function cardsGetTradeSentForLimit(
  state: State,
  now: number,
): { timestamp: number; cardId: string }[] {
  const trades = state.cards?.cardTrades?.cardsSent ?? [];

  const nowJST = getJSTDateTime(now).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  return trades.filter((trade) => {
    const tradeJST = getJSTDateTime(trade.timestamp).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return nowJST.diff(tradeJST, 'days').days < tradeLimits.send.durationDays;
  });
}

/**
 * Return whether the user can send another card within the current send limit.
 *
 * @param state User state containing card send history.
 * @param now Current timestamp used as the limit-window reference.
 * @returns True when the user has remaining send capacity.
 */
export function cardsCanSendCard(state: State, now: number): boolean {
  return cardsGetTradeSentForLimit(state, now).length < tradeLimits.send.amount;
}

/**
 * Return received-card trade records that still count against the receive
 * limit. Limit windows are measured by JST calendar days.
 *
 * @param state User state containing card receive history.
 * @param now Current timestamp used as the limit-window reference.
 * @returns Received-card trade records still inside the receive limit window.
 */
export function cardsGetTradeReceivedForLimit(
  state: State,
  now: number,
): { timestamp: number; cardId: string }[] {
  const trades = state.cards?.cardTrades?.cardsReceived ?? [];

  const nowJST = getJSTDateTime(now).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  return trades.filter((trade) => {
    const tradeJST = getJSTDateTime(trade.timestamp).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return (
      nowJST.diff(tradeJST, 'days').days < tradeLimits.receive.durationDays
    );
  });
}

/**
 * Return whether the user can receive another card within the current receive
 * limit.
 *
 * @param state User state containing card receive history.
 * @param now Current timestamp used as the limit-window reference.
 * @returns True when the user has remaining receive capacity.
 */
export function cardsCanReceiveCard(state: State, now: number): boolean {
  return (
    cardsGetTradeReceivedForLimit(state, now).length <
    tradeLimits.receive.amount
  );
}

/**
 * Return trade-start timestamps that still count against the daily trade limit.
 * Limit windows are measured by JST calendar days.
 *
 * @param state User state containing trade-start history.
 * @param now Current timestamp used as the limit-window reference.
 * @returns Trade-start timestamps still inside the trade limit window.
 */
export function cardsGetTradesStartedForLimit(
  state: State,
  now: number,
): number[] {
  const tradesStartedAt = state.cards?.cardTrades?.tradesStartedAt ?? [];

  const nowJST = getJSTDateTime(now).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  return tradesStartedAt.filter((tradeStartedAt) => {
    const tradeJST = getJSTDateTime(tradeStartedAt).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return nowJST.diff(tradeJST, 'days').days < tradeLimits.trade.durationDays;
  });
}

/**
 * Return whether the user can initiate another trade during the current day.
 *
 * @param state User state containing trade-start history.
 * @param now Current timestamp used as the trade-limit reference.
 * @returns True when the user has remaining trade initiation capacity.
 */
export function cardsCanStartTrade(state: State, now: number): boolean {
  return (
    cardsGetTradesStartedForLimit(state, now).length < tradeLimits.trade.amount
  );
}
